import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Activity } from '../../../../../lib/common/custom-activity/eventTypes';
import { trigger } from '../../../../../lib/common/postmonger/postmongerManager';

export interface DataSelectorState {
    statusCode: number | null;

    dirty: boolean;
}

const initialState: DataSelectorState = {
    statusCode: null,

    dirty: false,
};

export const dataSelectorSlice = createSlice({
    name: 'dataSelector',
    initialState,
    reducers: {
        updateStateFromActivity: (
            state: DataSelectorState,
            action: PayloadAction<Activity>,
        ) => {
            // Update the state to match the activity.
            const activity = action.payload;

            state.statusCode = getInArgumentValue(activity, 'statusCode');

            state.dirty = false;
            trigger('setActivityDirtyState', false);
        },
        statusCodeChanged: (
            state: DataSelectorState,
            selection: PayloadAction<string>,
        ) => {
            // Try to parse the input status. If it's empty, we'll
            // clear out the saved status. If it's invalid, we'll keep
            // the current value.
            const parsedPayload =
                selection.payload == ''
                    ? null
                    : Number.parseInt(selection.payload);

            if (
                !Number.isNaN(parsedPayload) &&
                state.statusCode !== parsedPayload
            ) {
                state.statusCode = parsedPayload;

                state.dirty = true;
                trigger('setActivityDirtyState', true);
            }
        },
        done: (state: DataSelectorState, action: PayloadAction<Activity>) => {
            if (!state.dirty) {
                console.warn('Nothing to save.');
                return;
            }

            // Check to make sure all required fields are filled out
            // if (
            //     state.statusCode === null
            // ) {
            //     console.error('Invalid selection.');
            //     return;
            // }

            // Since the action is passed in from the component, rather than being
            // read from state directly, it is immutable. Duplicate it so we can add our
            // arguments and update Journey Builder.
            const activity = JSON.parse(JSON.stringify(action.payload));

            addOrUpdateInArgument(activity, 'statusCode', state.statusCode);

            activity['metaData'].isConfigured = true;

            trigger('updateActivity', activity);
            trigger('requestInspectorClose');
        },
        cancel: (state: DataSelectorState) => {
            // Tell Journey Builder that this activity has no changes.
            // we wont be prompted to save changes when the inspector closes
            trigger('setActivityDirtyState', false);

            // now request that Journey Builder closes the inspector/drawer
            trigger('requestInspectorClose');
        },
    },
});

export const { updateStateFromActivity, statusCodeChanged, done, cancel } =
    dataSelectorSlice.actions;

function getInArgumentValue(activity: Activity, argument: string): any | null {
    if (activity?.arguments?.execute?.inArguments === undefined) {
        return null;
    }

    const inArguments = activity.arguments.execute.inArguments;

    // See if we already have an argument with this name. We'll update the value if so.
    let executeArg = inArguments.find((item: any) => argument in item);

    // Bail if the argument doesn't exist
    if (executeArg === undefined || !(argument in executeArg)) {
        return null;
    }

    return executeArg[argument];
}

/**
 * Adds or updates an inArgument with the name `argument` and value `value`.
 * @param activity The activity to update
 * @param argument The argument name
 * @param value The argument value
 * @returns Whether anything was changed.
 */
function addOrUpdateInArgument(
    activity: Activity,
    argument: string,
    value: any,
): boolean {
    if (activity?.arguments?.execute?.inArguments === undefined) {
        return false;
    }

    const inArguments = activity.arguments.execute.inArguments;

    // See if we already have an argument with this name. We'll update the value if so.
    let executeArg = inArguments.find((item: any) => argument in item);

    // Bail if we don't need to change anything
    if (
        executeArg !== undefined &&
        argument in executeArg &&
        executeArg[argument] === value
    ) {
        return false;
    }

    if (executeArg === undefined) {
        executeArg = {};
        inArguments.push(executeArg);
    }

    executeArg[argument] = value;
    return true;
}
